import React, { createContext, useContext, useEffect, useState } from "react";

import { getMultiplierInfo, getMultiplierReferrals } from "@/api/base";
import { MultiplierInfo, MultiplierReferralsPage } from "@/types/referals";
import { useUserInfoStore } from "@/store/userInfoStore";

interface MultiplierContextValue {
  multiplierLoading: boolean;
  multiplierReferralsLoading: boolean;
  multiplierInfo: MultiplierInfo | null;
  multiplierReferrals: MultiplierReferralsPage | null;
  multiplierCelebration: "activated" | "extended" | null;
  loadMoreMultiplierReferrals: () => Promise<void>;
  dismissMultiplierCelebration: () => void;
}

type MultiplierSnapshot = {
  multiplier: number | null;
  expiresAt: string | null;
};

const MultiplierContext = createContext<MultiplierContextValue | undefined>(
  undefined
);

export const MultiplierProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const yandexId = useUserInfoStore((state) => state.yandexId);
  const [multiplierLoading, setMultiplierLoading] = useState(false);
  const [multiplierReferralsLoading, setMultiplierReferralsLoading] = useState(
    false
  );
  const [multiplierInfo, setMultiplierInfo] = useState<MultiplierInfo | null>(
    null
  );
  const [
    multiplierReferrals,
    setMultiplierReferrals,
  ] = useState<MultiplierReferralsPage | null>(null);
  const [multiplierCelebration, setMultiplierCelebration] = useState<
    "activated" | "extended" | null
  >(null);
  const trackMultiplierChange = (info: MultiplierInfo) => {
    const storageKey = `referral-multiplier:${yandexId}`;
    const nextSnapshot: MultiplierSnapshot = {
      multiplier: info.current.multiplier,
      expiresAt: info.current.expires_at,
    };

    try {
      const storedSnapshot = window.localStorage.getItem(storageKey);
      if (storedSnapshot) {
        const previousSnapshot = JSON.parse(
          storedSnapshot
        ) as MultiplierSnapshot;

        if (
          previousSnapshot.multiplier === null &&
          nextSnapshot.multiplier !== null
        ) {
          setMultiplierCelebration("activated");
        } else if (
          previousSnapshot.multiplier !== null &&
          nextSnapshot.multiplier !== null &&
          previousSnapshot.expiresAt !== nextSnapshot.expiresAt
        ) {
          setMultiplierCelebration("extended");
        }
      }

      window.localStorage.setItem(storageKey, JSON.stringify(nextSnapshot));
    } catch (error) {
      console.error(error);
    }
  };

  const loadReferrals = async (offset: number = 0) => {
    setMultiplierReferralsLoading(true);

    try {
      const page = await getMultiplierReferrals(yandexId, 10, offset);
      setMultiplierReferrals((currentPage) =>
        offset === 0 || !currentPage
          ? page
          : {
              ...page,
              results: [...currentPage.results, ...page.results],
            }
      );
    } catch (error) {
      console.error(error);
    } finally {
      setMultiplierReferralsLoading(false);
    }
  };

  const loadMoreMultiplierReferrals = async () => {
    if (
      !multiplierReferrals ||
      multiplierReferralsLoading ||
      multiplierReferrals.results.length >= multiplierReferrals.count
    ) {
      return;
    }

    await loadReferrals(multiplierReferrals.results.length);
  };

  useEffect(() => {
    if (!yandexId) {
      setMultiplierInfo(null);
      setMultiplierReferrals(null);
      setMultiplierLoading(false);
      return;
    }

    const loadMultiplier = async () => {
      setMultiplierLoading(true);

      try {
        const info = await getMultiplierInfo(yandexId);
        setMultiplierInfo(info);
        trackMultiplierChange(info);

        if (info.available) {
          await loadReferrals();
        } else {
          setMultiplierReferrals(null);
        }
      } catch (error) {
        console.error(error);
        setMultiplierInfo(null);
        setMultiplierReferrals(null);
      } finally {
        setMultiplierLoading(false);
      }
    };

    loadMultiplier();
  }, [yandexId]);

  return (
    <MultiplierContext.Provider
      value={{
        multiplierLoading,
        multiplierReferralsLoading,
        multiplierInfo,
        multiplierReferrals,
        multiplierCelebration,
        loadMoreMultiplierReferrals,
        dismissMultiplierCelebration: () => setMultiplierCelebration(null),
      }}
    >
      {children}
    </MultiplierContext.Provider>
  );
};

export const useMultiplierDataContext = (): MultiplierContextValue => {
  const context = useContext(MultiplierContext);
  if (!context) {
    throw new Error(
      "useMultiplierDataContext must be used within MultiplierProvider"
    );
  }
  return context;
};
